import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ClerkProvider, useUser } from '@clerk/clerk-react';
import { Provider } from 'react-redux';
import { store } from './store';
import LandingPage from './LandingPage';
import Login from './Login';
import Home from './Home';
import MultiStepSignupScreen from './components/MultiStepSignupModal';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import AboutUs from './components/AboutUs';


const PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error("REACT_APP_CLERK_PUBLISHABLE_KEY is required in environment variables");
}

// TypeScript assertion - we know it's defined after the check above
const clerkPublishableKey: string = PUBLISHABLE_KEY;

// Wrapper for protected routes using Clerk authentication only
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded, user } = useUser();
  
  console.log('🛡️ ProtectedRoute check:', {
    isSignedIn,
    isLoaded,
    userExists: !!user,
    currentPath: window.location.pathname,
    timestamp: new Date().toISOString()
  });
  
  // If Clerk is still loading, show loading
  if (!isLoaded) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#667eea'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div>Loading...</div>
          <div style={{ fontSize: '12px', marginTop: '8px', color: '#a0a0a0' }}>
            Checking authentication status...
          </div>
        </div>
      </div>
    );
  }
  
  // If user is not signed in with Clerk, redirect to login
  if (!isSignedIn) {
    console.log('🚫 User not authenticated with Clerk, redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  console.log('✅ User authenticated with Clerk, allowing access');
  return <>{children}</>;
}

function App() {
  return (
    <Provider store={store}>
      <ClerkProvider 
        publishableKey={clerkPublishableKey}
        appearance={{
          elements: {
            formButtonPrimary: 'bg-blue-600 hover:bg-blue-700 text-sm',
            card: 'shadow-none',
            headerTitle: 'text-xl font-semibold text-gray-900',
          }
        }}
      >
        <Router>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginRedirect />} />
            <Route path="/signup" element={<SignupRedirect />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/home" element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </ClerkProvider>
    </Provider>
  );
}

// Redirect to /home if already signed in
function LoginRedirect() {
  const { isSignedIn, isLoaded } = useUser();
  
  console.log('🔄 LoginRedirect check:', {
    isSignedIn,
    isLoaded
  });
  
  if (!isLoaded) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#667eea'
      }}>
        Loading...
      </div>
    );
  }
  
  if (isSignedIn) {
    console.log('User already authenticated with Clerk, redirecting to home');
    return <Navigate to="/home" replace />;
  }
  
  return <Login />;
}

// Redirect to /home if already signed in
function SignupRedirect() {
  const { isSignedIn, isLoaded } = useUser();
  
  if (!isLoaded) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#667eea'
      }}>
        Loading...
      </div>
    );
  }
  
  return isSignedIn ? <Navigate to="/home" replace /> : <MultiStepSignupScreen />;
}

export default App;
